import { NavLink, Link, useNavigate } from "react-router-dom";
import {
  ChartBar, Users, Buildings, CreditCard, EnvelopeSimple,
  ShieldCheck, Flag, Wrench, SignOut, Warning, ChartLine, Key, Robot,
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const LINKS = [
  { to: "/superadmin", label: "Dashboard", icon: ChartBar, end: true },
  { to: "/superadmin/users", label: "Utilisateurs", icon: Users },
  { to: "/superadmin/organizations", label: "Organisations", icon: Buildings },
  { to: "/superadmin/stripe", label: "Stripe", icon: CreditCard },
  { to: "/superadmin/emails", label: "Emails", icon: EnvelopeSimple },
  { to: "/superadmin/audit", label: "Audit RGPD", icon: ShieldCheck },
  { to: "/superadmin/flags", label: "Feature flags", icon: Flag },
  { to: "/superadmin/maintenance", label: "Maintenance", icon: Wrench },
  { to: "/superadmin/moderation", label: "Modération", icon: Warning },
  { to: "/superadmin/metrics", label: "Metrics produit", icon: ChartLine },
  { to: "/superadmin/llm", label: "Coût IA", icon: Robot },
  { to: "/superadmin/security", label: "Sécurité 2FA", icon: Key },
];

export default function SuperadminLayout({ children, title, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-[#0B1B2E] text-white flex" data-testid="superadmin-layout">
      <aside className="hidden md:flex flex-col w-[280px] bg-[#081221] border-r border-white/10 px-5 py-6 fixed h-full">
        <Link to="/superadmin" className="flex items-center gap-2.5 mb-2" data-testid="superadmin-home-link">
          <span className="w-8 h-8 rounded-[10px] bg-[#E4572E] flex items-center justify-center text-white font-bold text-base">Q</span>
          <div>
            <span className="text-[16px] font-bold tracking-tight text-white block leading-none">Questiomed</span>
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#E4572E] font-semibold">Backoffice</span>
          </div>
        </Link>
        <p className="text-[11px] text-[#8494A8] mb-8 mt-2 leading-relaxed">
          Réservé aux administrateurs plateforme. Toutes les actions sont tracées (RGPD Art. 32).
        </p>
        <nav className="space-y-1 flex-1">
          {LINKS.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`superadmin-nav-${to.split("/").pop() || "dashboard"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2.5 rounded-[10px] text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#E4572E] text-white"
                    : "text-[#B7C4D6] hover:bg-white/[0.06] hover:text-white"
                }`
              }
            >
              <Icon size={18} weight="regular" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="pt-4 border-t border-white/10 mt-4">
          <div className="px-2 py-2 space-y-1">
            <p className="text-sm font-medium text-white leading-snug" data-testid="superadmin-user-name">
              {user?.name}
            </p>
            <p className="text-[11px] text-[#8494A8] leading-snug break-all">{user?.email}</p>
            <span className="inline-block px-1.5 py-0.5 rounded bg-[#E4572E]/25 text-[#FFB59E] text-[10px] tracking-wider uppercase font-semibold">
              Superadmin
            </span>
          </div>
          <Link to="/dashboard" className="block px-2 py-1.5 text-xs text-[#B7C4D6] hover:text-white transition" data-testid="superadmin-goto-app">
            ← Retour à l'app praticien
          </Link>
          <Button onClick={doLogout} variant="ghost" size="sm" className="w-full justify-start text-[#B7C4D6] hover:bg-white/[0.06] hover:text-white mt-1" data-testid="superadmin-logout-btn">
            <SignOut size={16} className="mr-2" /> Se déconnecter
          </Button>
        </div>
      </aside>

      <main className="flex-1 md:ml-[280px]">
        <div className="px-12 pt-10 pb-12 max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
            <h1 className="text-[32px] font-bold tracking-tight text-white leading-tight" data-testid="superadmin-page-title">
              {title}
            </h1>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}

export function SuperadminProtected({ children }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1B2E]">
        <div className="flex gap-2">
          <span className="w-2 h-2 rounded-full bg-[#E4572E] animate-pulse"></span>
          <span className="w-2 h-2 rounded-full bg-[#E4572E] animate-pulse" style={{ animationDelay: "0.2s" }}></span>
          <span className="w-2 h-2 rounded-full bg-[#E4572E] animate-pulse" style={{ animationDelay: "0.4s" }}></span>
        </div>
      </div>
    );
  }
  if (!user) {
    nav("/login", { replace: true });
    return null;
  }
  if (!user.is_superadmin) {
    nav("/dashboard", { replace: true });
    return null;
  }
  if (user._impersonating) {
    // Cannot access backoffice while impersonating
    nav("/dashboard", { replace: true });
    return null;
  }
  return children;
}

// Reusable dark-theme card
export function DarkCard({ children, className = "", ...props }) {
  return (
    <div className={`bg-[#0F2136] border border-white/10 rounded-2xl ${className}`} {...props}>
      {children}
    </div>
  );
}

// Reusable KPI tile
export function Kpi({ label, value, sub, testid }) {
  return (
    <DarkCard className="p-6" data-testid={testid}>
      <p className="text-xs tracking-[0.2em] uppercase text-[#8494A8] mb-2">{label}</p>
      <p className="text-3xl font-bold tracking-tight text-white">{value}</p>
      {sub && <p className="text-xs text-[#8494A8] mt-1.5">{sub}</p>}
    </DarkCard>
  );
}
